import type { SessionTokens } from '@/auth/authStore';

export interface AuthFetchDeps {
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  getTenantId: () => string | null;
  onRefreshed: (tokens: SessionTokens) => void;
  onAuthFailed: () => void;
  baseUrl: string;
  fetchImpl?: typeof fetch;
}

const AUTH_PATHS = ['/v1/auth/login', '/v1/auth/refresh', '/v1/auth/logout'];

function isAuthEndpoint(url: string): boolean {
  try {
    const path = new URL(url).pathname;
    return AUTH_PATHS.some((p) => path.endsWith(p));
  } catch {
    return false;
  }
}

/**
 * Fetch wrapper used by the generated client: adds the bearer token and X-Tenant-Id, and on 401 performs a
 * single-flight refresh followed by one retry. Concurrent 401s share the same refresh call.
 */
export function createAuthFetch(deps: AuthFetchDeps): (input: Request) => Promise<Response> {
  const doFetch = deps.fetchImpl ?? ((req: Request) => globalThis.fetch(req));
  let refreshing: Promise<boolean> | null = null;

  function decorate(req: Request): Request {
    const headers = new Headers(req.headers);
    const token = deps.getAccessToken();
    if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
    const tenant = deps.getTenantId();
    if (tenant && !headers.has('X-Tenant-Id')) headers.set('X-Tenant-Id', tenant);
    return new Request(req, { headers });
  }

  async function refresh(): Promise<boolean> {
    const refreshToken = deps.getRefreshToken();
    if (!refreshToken) return false;
    try {
      const res = await doFetch(
        new Request(`${deps.baseUrl}/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        }),
      );
      if (!res.ok) return false;
      const tokens = (await res.json()) as SessionTokens;
      deps.onRefreshed(tokens);
      return true;
    } catch {
      return false;
    }
  }

  function refreshOnce(): Promise<boolean> {
    refreshing ??= refresh().finally(() => {
      refreshing = null;
    });
    return refreshing;
  }

  return async (input: Request) => {
    const retryCopy = input.clone();
    const res = await doFetch(decorate(input));
    if (res.status !== 401 || isAuthEndpoint(input.url)) return res;

    const ok = await refreshOnce();
    if (!ok) {
      deps.onAuthFailed();
      return res;
    }
    const headers = new Headers(retryCopy.headers);
    headers.delete('Authorization');
    return doFetch(decorate(new Request(retryCopy, { headers })));
  };
}
